import React, { useMemo } from "react";
import Chart from "react-apexcharts";

import { ReactComponent as ArrowUpIcon } from "../../../../../vendor/_new/icon/overview/arrow_up.svg";
import { ReactComponent as ArrowDownIcon } from "../../../../../vendor/_new/icon/overview/arrow_down.svg";
import colors from "../../../../../utils/colors";
import { withApexChartFont, apexNoOfYAxisTitle } from "../../../../../utils/apexChartDefaults";
import { getLabelForLatestDetections } from "..";

import { Stack, Typography, CircularProgress } from "@mui/material";
import { SeverityLevel } from "../../../../../utils/constants";
import useHome from "../../../../../hooks/user/useHome";
import { getPercentageFor2Values, formatPercentChange } from "../../../../../utils/functions";
import { detectionPercentColor, isDetectionTrendUp } from "../overviewPercentUtils";
import {
  apiBucketsToPoints,
  prepareOverviewSeries,
  overviewIntervalMs,
  overviewRangeMs,
} from "../overviewChartSeries";

const getLabelBySeverity = (severity) => {
  switch (severity) {
    case SeverityLevel.CRITICAL:
      return "Critical";
    case SeverityLevel.ERROR:
      return "High";
    case SeverityLevel.WARNING:
      return "Medium";
    case SeverityLevel.NOTICE:
      return "Low";
    default:
      return "Unknown";
  }
};

const getSeverityKey = (severity) => {
  switch (severity) {
    case SeverityLevel.CRITICAL:
      return "critical";
    case SeverityLevel.ERROR:
      return "high";
    case SeverityLevel.WARNING:
      return "medium";
    case SeverityLevel.NOTICE:
      return "low";
    default:
      return "critical";
  }
};

const DetectionBySeverity = ({ severity = SeverityLevel.CRITICAL }) => {
  const { detectionsSeverityTimeseries, latestDetects, time_range } = useHome();

  const chartPoints = useMemo(() => {
    if (!detectionsSeverityTimeseries) return [];
    const key = getSeverityKey(severity);
    const raw = apiBucketsToPoints(detectionsSeverityTimeseries[key] || []);
    const { fromMs, toMs } = overviewRangeMs(time_range);
    const intervalMs = overviewIntervalMs(time_range?.period || "7d");
    return prepareOverviewSeries(raw, { fromMs, toMs, intervalMs });
  }, [severity, detectionsSeverityTimeseries, time_range]);

  const sparseSpike = chartPoints.length <= 5;

  const series = useMemo(
    () => [
      {
        name: "Detection by Severity",
        data: chartPoints,
      },
    ],
    [chartPoints]
  );

  const percentageValue = useMemo(() => {
    const key = getSeverityKey(severity);
    const past = latestDetects?.[key]?.past ?? 0;
    const now = latestDetects?.[key]?.now ?? 0;
    return getPercentageFor2Values(past, now);
  }, [severity, latestDetects]);

  const strokeColor =
    severity === SeverityLevel.CRITICAL ? "rgba(242, 137, 137, 1)" : "rgba(255, 219, 67, 1)";
  const fillToColor =
    severity === SeverityLevel.CRITICAL ? "rgba(242, 137, 137, 0.25)" : "rgba(255, 219, 67, 0.25)";

  const options = withApexChartFont({
    chart: {
      type: "area",
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: true },
      sparkline: { enabled: false },
      foreColor: "#696969",
    },
    dataLabels: {
      enabled: false,
    },
    colors: [strokeColor],
    stroke: {
      curve: "smooth",
      width: 2,
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 1,
        inverseColors: false,
        gradientToColors: [fillToColor],
        opacityFrom: 0.55,
        opacityTo: 0.05,
        stops: [0, 100],
      },
    },
    markers: {
      size: sparseSpike ? 4 : 0,
      hover: { size: 6 },
      colors: [strokeColor],
      strokeColors: "#fff",
      strokeWidth: 2,
    },
    xaxis: {
      type: "datetime",
      labels: {
        datetimeUTC: false,
        style: { fontSize: "12px", colors: "#696969" },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
      tickAmount: 2,
    },
    yaxis: {
      tickAmount: 4,
      min: 0,
      forceNiceScale: true,
      title: apexNoOfYAxisTitle("No. of Detections"),
      labels: {
        style: { colors: "#696969", fontSize: "12px" },
        formatter: (val) => Math.round(val),
      },
    },
    grid: {
      show: true,
      borderColor: "#e6e6e6",
      strokeDashArray: 0,
      padding: { left: 8 },
      xaxis: { lines: { show: false } },
      yaxis: {
        lines: { show: true },
      },
    },
    tooltip: {
      enabled: true,
      shared: false,
      followCursor: true,
      x: { show: false },
      custom: function ({ series, seriesIndex, dataPointIndex }) {
        const value = series[seriesIndex][dataPointIndex];
        let arrow = "";
        let arrowClass = "";
        if (dataPointIndex > 0) {
          const prev = series[seriesIndex][dataPointIndex - 1];
          if (value >= prev) {
            arrow = "▲";
            arrowClass = "up";
          } else {
            arrow = "▼";
            arrowClass = "down";
          }
        }
        return `
          <div class="custom-tooltip">
            <div class="value-row">
              <span class="value-text ${arrowClass}">${value} ${arrow}</span>
            </div>
          </div>
        `;
      },
    },
    legend: { show: false },
  });

  return detectionsSeverityTimeseries === null ? (
    <Stack alignItems={"center"} justifyContent={"center"} sx={{ height: "100%" }}>
      <CircularProgress sx={{ color: colors.neutral[400] }} />
    </Stack>
  ) : (
    <Stack sx={{ mt: 4 }}>
      <Stack direction={"row"} spacing={2} sx={{ pl: 12 }}>
        {getLabelForLatestDetections(getLabelBySeverity(severity))}
        <Stack direction={"row"} alignItems={"center"}>
          <Typography
            sx={{
              fontSize: 14,
              lineHeight: "21px",
              color: detectionPercentColor(percentageValue),
            }}
          >
            {formatPercentChange(percentageValue)}
          </Typography>
          {isDetectionTrendUp(percentageValue) ? (
            <ArrowUpIcon style={{ color: colors.red[100], width: 20, height: 20 }} />
          ) : percentageValue === 0 ? null : (
            <ArrowDownIcon style={{ color: "#1DD75B", width: 20, height: 20 }} />
          )}
        </Stack>
      </Stack>
      <Chart options={options} series={series} type="area" height={210} />
    </Stack>
  );
};
export default DetectionBySeverity;
